import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { Button, Modal } from 'antd';
import { get } from 'lodash';

import SupportedMediaTooltip from 'containers/QuestionnairePage/NodeSettings/SupportedMediaTooltip';
import FileUpload from 'components/FileUpload/Loadable';
import {
  SUPPORTED_TYPES,
  IMAGE_FILE,
  AUDIO_FILE,
  VIDEO_FILE,
  SVG_FILE,
} from 'components/FileUpload/constants';

import Pficon from 'components/Pficon';
import { usePrevious } from 'utils/hooks';
import messages from './messages';

const whiteBtnClasses = classNames(
  'background-grey-1',
  'text-weight-bold',
  'color-grey-10-hover',
  'border-radius-level-1',
  'border-grey-5',
  'border-grey-6-hover',
  'box-shadow-none',
  'outline-blue',
);

function UploadModal({
  visible,
  media = {},
  hideModal,
  saveMedia,
  removeMedia,
  title,
  mask = true,
  uploadUrl,
  supportedMedia = [],
}) {
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [temporaryMedia, setTemporaryMedia] = useState(media || {});

  const previousVisible = usePrevious(visible);

  const onAddMedia = useCallback(
    ({ url, thumbnail, type, filename, disableControl, success }) => {
      if (success) {
        setTemporaryMedia({
          url,
          thumbnail,
          type,
          filename,
          success,
          disableControl,
        });
      }
    },
    [],
  );

  const onRemoveMedia = useCallback(() => {
    setTemporaryMedia({});
  }, []);

  const onStartUpload = useCallback(() => setSubmitDisabled(true), []);
  const onCompleteUpload = useCallback(
    (payload) => {
      onAddMedia(payload);
      setSubmitDisabled(false);
    },
    [onAddMedia],
  );

  const onSave = useCallback(() => {
    const currentMediaUrl = get(media, 'url', '');
    const newMediaUrl = get(temporaryMedia, 'url', '');
    const currentControlsDisabled = get(media, 'disableControl', '');
    const newControlsDisabled = get(temporaryMedia, 'disableControl', '');
    const hasChangedControls = currentControlsDisabled !== newControlsDisabled;
    const hasChangedImage = currentMediaUrl !== newMediaUrl;
    const isMediaCleared = !newMediaUrl;

    if (currentMediaUrl && hasChangedImage) {
      removeMedia(media);
    }

    // Clearing media is fully handled by removeMedia. Do not persist an empty
    // media object — peeq upsert rejects media without type/url/thumbnail.
    if (hasChangedImage && isMediaCleared) {
      hideModal();
      return;
    }

    if (hasChangedControls || hasChangedImage) {
      saveMedia(temporaryMedia);
    }

    hideModal();
  }, [media, temporaryMedia, removeMedia, saveMedia, hideModal]);

  useEffect(() => {
    if (visible === previousVisible) return;

    if (!visible) {
      setSubmitDisabled(false);
      setTemporaryMedia({});
    } else {
      setTemporaryMedia(media || {});
    }
  }, [visible, media, previousVisible]);

  const containsImageOrVector =
    supportedMedia.includes(SUPPORTED_TYPES[IMAGE_FILE]) ||
    supportedMedia.includes(SUPPORTED_TYPES[SVG_FILE]);
  return (
    <Modal
      wrapClassName="upload-modal-wrap"
      className="move-questions-modal"
      visible={visible}
      onCancel={hideModal}
      mask={mask}
      title={
        Boolean(title) && (
          <p className="margin-none text-size-primary text-weight-bold">
            {title}
          </p>
        )
      }
      footer={null}
      closeIcon={
        <Pficon
          type="close"
          className="color-grey-10 color-grey-8-hover transition-normal"
        />
      }
      destroyOnClose
    >
      <p className="text-size-secondary color-grey-10">
        <FormattedMessage {...messages.modal.labelUpload} />
      </p>

      <div className="margin-btm-lg">
        <div className="flex flex-items-center padding-horizontal-md padding-vertical-sm border-grey-5 border-radius-top-level-1 border-bottom-none">
          <SupportedMediaTooltip supportedMedia={supportedMedia}>
            {containsImageOrVector && (
              <Pficon
                type="ab-image"
                className="margin-right-sm color-grey-10 text-size-h3"
                title="image"
              />
            )}
            {supportedMedia.includes(SUPPORTED_TYPES[VIDEO_FILE]) && (
              <Pficon
                type="ab-video"
                className="margin-right-sm color-grey-10 text-size-h3"
                title="video"
              />
            )}
            {supportedMedia.includes(SUPPORTED_TYPES[AUDIO_FILE]) && (
              <Pficon
                type="ab-sound"
                className="color-grey-10 text-size-h3"
                title="sound"
              />
            )}
          </SupportedMediaTooltip>
        </div>

        <FileUpload
          uploadUrl={uploadUrl}
          filePath={temporaryMedia.url || ''}
          thumbnail={temporaryMedia.thumbnail}
          mediaType={temporaryMedia.type}
          disableControl={temporaryMedia.disableControl}
          onStartUpload={onStartUpload}
          onCompleteUpload={onCompleteUpload}
          onRemove={onRemoveMedia}
          size="full"
          messageClassName=""
          message={<FormattedHTMLMessage {...messages.modal.uploadMessage} />}
          supportedMedia={supportedMedia}
          visible={visible}
          doNotShowHints
        />
      </div>

      <footer className="flex flex-justify-between">
        <Button className={whiteBtnClasses} onClick={hideModal}>
          <FormattedMessage {...messages.modal.cancel} />
        </Button>

        <Button
          disabled={submitDisabled}
          className={classNames(
            'color-grey-1 color-grey-1-hover background-blue-6-hover',
            'background-blue-6 border-blue-6 border-blue-6-hover',
            'box-shadow-none border-radius-level-1 text-weight-bold margin-left-sm',
            'outline-blue',
            {
              'opacity-50': submitDisabled,
            },
          )}
          onClick={onSave}
        >
          <FormattedMessage {...messages.modal.submit} />
        </Button>
      </footer>
    </Modal>
  );
}

UploadModal.propTypes = {
  visible: PropTypes.bool,
  media: PropTypes.object,
  hideModal: PropTypes.func,
  removeMedia: PropTypes.func,
  updateAttributeValue: PropTypes.func,
  maskStyle: PropTypes.object,
  supportedMedia: PropTypes.array,
};

export default UploadModal;
